import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Button, Table, Tag, Typography, Space, message, Spin, Popconfirm } from 'antd';
import {
  UploadOutlined,
  FileOutlined,
  DownloadOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  EditOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import dayjs from 'dayjs';
import { documentApi, type DocumentDto } from '@/api/documentApi';

const { Text, Title } = Typography;

interface Props {
  opportunityId: string;
  onUploadComplete?: () => void;
}

const getFileIcon = (fileType?: string) => {
  if (!fileType) return <FileOutlined />;
  if (fileType.includes('pdf')) return <FilePdfOutlined style={{ color: 'var(--color-error)' }} />;
  if (fileType.includes('word') || fileType.includes('document'))
    return <FileWordOutlined style={{ color: 'var(--color-primary)' }} />;
  if (fileType.includes('excel') || fileType.includes('spreadsheet'))
    return <FileExcelOutlined style={{ color: 'var(--color-success)' }} />;
  return <FileOutlined />;
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const PhaseDDraftUploads: React.FC<Props> = ({ opportunityId, onUploadComplete }) => {
  const [docs, setDocs] = useState<DocumentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadingInitial, setUploadingInitial] = useState(false);
  const [uploadingFinal, setUploadingFinal] = useState(false);

  // All authenticated users can delete documents
  const canDelete = (_doc: DocumentDto) => true;

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const all = await documentApi.getByPhase(opportunityId, 'PHASE_D');
      setDocs(all.filter((d) => d.category === 'INITIAL_DRAFT' || d.category === 'FINAL_DRAFT'));
    } catch (err) {
      console.error('Failed to fetch draft documents:', err);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const makeUploadHandler = (
    category: 'INITIAL_DRAFT' | 'FINAL_DRAFT',
    setBusy: (b: boolean) => void,
  ): UploadProps['customRequest'] => async (options) => {
    const { file, onSuccess, onError } = options;
    setBusy(true);
    try {
      await documentApi.uploadFile(file as File, opportunityId, category, 'PHASE_D');
      message.success(`Uploaded ${(file as File).name}`);
      onSuccess?.({});
      fetchDocs();
      onUploadComplete?.();
    } catch (err) {
      console.error('Upload failed:', err);
      message.error('Failed to upload file');
      onError?.(err as Error);
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async (doc: DocumentDto) => {
    try {
      await documentApi.downloadFile(doc.id, doc.fileName);
    } catch {
      message.error('Failed to download file');
    }
  };

  const handleDelete = async (docId: string) => {
    try {
      await documentApi.deleteDocument(docId);
      message.success('File deleted');
      fetchDocs();
      onUploadComplete?.();
    } catch {
      message.error('Failed to delete file');
    }
  };

  const initialDocs = docs.filter((d) => d.category === 'INITIAL_DRAFT');
  const finalDocs = docs.filter((d) => d.category === 'FINAL_DRAFT');

  const renderDocList = (list: DocumentDto[]) => {
    if (loading) return <Spin size="small" />;
    if (list.length === 0) {
      return <Text type="secondary" style={{ fontSize: 12 }}>No files uploaded yet.</Text>;
    }
    return (
      <Table<DocumentDto>
        size="small"
        dataSource={list}
        rowKey="id"
        pagination={false}
        scroll={{ x: 480 }}
        columns={[
          {
            title: 'File Name',
            dataIndex: 'fileName',
            key: 'fileName',
            width: 200,
            sorter: (a, b) => (a.fileName || '').localeCompare(b.fileName || ''),
            render: (name: string, doc) => (
              <Space size={6}>
                {getFileIcon(doc.fileType)}
                <Text
                  style={{ fontSize: 12, maxWidth: 160 }}
                  ellipsis={{ tooltip: name }}
                >
                  {name}
                </Text>
              </Space>
            ),
          },
          {
            title: 'Size',
            dataIndex: 'fileSizeBytes',
            key: 'size',
            width: 70,
            sorter: (a, b) => (a.fileSizeBytes ?? 0) - (b.fileSizeBytes ?? 0),
            render: (b: number) => <Text style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{formatFileSize(b)}</Text>,
          },
          {
            title: 'Uploaded By',
            dataIndex: 'uploadedBy',
            key: 'uploadedBy',
            width: 110,
            sorter: (a, b) => (a.uploadedBy || '').localeCompare(b.uploadedBy || ''),
            render: (v: string) => (
              <Text style={{ fontSize: 11 }} ellipsis={{ tooltip: v }}>
                {v || '—'}
              </Text>
            ),
          },
          {
            title: 'Date Uploaded',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 130,
            defaultSortOrder: 'descend' as const,
            sorter: (a, b) => {
              const aT = a.createdAt ? dayjs(a.createdAt).unix() : 0;
              const bT = b.createdAt ? dayjs(b.createdAt).unix() : 0;
              return aT - bT;
            },
            render: (v: string) => (
              <Text style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                {v ? dayjs(v).format('MM/DD/YYYY h:mm A') : '—'}
              </Text>
            ),
          },
          {
            title: '',
            key: 'actions',
            width: 70,
            render: (_v, doc) => (
              <Space size={4}>
                <Button
                  size="small"
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(doc)}
                />
                {canDelete(doc) && (
                  <Popconfirm
                    title="Delete file?"
                    onConfirm={() => handleDelete(doc.id)}
                    okText="Delete"
                    okType="danger"
                  >
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />
    );
  };

  const cardStyle: React.CSSProperties = {
    flex: 1,
    background: 'var(--color-bg-base)',
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    padding: 16,
    minWidth: 0,
  };

  return (
    <div style={{ marginTop: 16, marginBottom: 16 }}>
      <Title level={5} style={{ margin: '0 0 12px', color: 'var(--color-text-primary)' }}>
        Proposal Draft Documents
      </Title>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
        Upload draft proposal documents. Each will be evaluated against the original RFO/RFP intake
        details (agency, requirements, due date, etc.) when AI Analysis runs.
      </Text>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {/* Initial / Review Stage */}
        <div style={cardStyle}>
          <Space style={{ marginBottom: 8 }}>
            <EditOutlined style={{ color: 'var(--color-primary)' }} />
            <Text strong>Initial / Review Stage Drafts</Text>
            {initialDocs.length > 0 && <Tag color="blue">{initialDocs.length}</Tag>}
          </Space>
          <div style={{ marginBottom: 12 }}>
            <Upload
              multiple
              showUploadList={false}
              customRequest={makeUploadHandler('INITIAL_DRAFT', setUploadingInitial)}
            >
              <Button type="primary" icon={<UploadOutlined />} loading={uploadingInitial}>
                Upload Initial Drafts
              </Button>
            </Upload>
          </div>
          {renderDocList(initialDocs)}
        </div>

        {/* Final Draft Submission */}
        <div style={cardStyle}>
          <Space style={{ marginBottom: 8 }}>
            <CheckCircleOutlined style={{ color: 'var(--color-success)' }} />
            <Text strong>Final Draft for Submission</Text>
            {finalDocs.length > 0 && <Tag color="green">{finalDocs.length}</Tag>}
          </Space>
          <div style={{ marginBottom: 12 }}>
            <Upload
              multiple
              showUploadList={false}
              customRequest={makeUploadHandler('FINAL_DRAFT', setUploadingFinal)}
            >
              <Button
                icon={<UploadOutlined />}
                loading={uploadingFinal}
                style={{ background: 'var(--color-success)', color: 'var(--color-text-inverse)', borderColor: 'var(--color-success)' }}
              >
                Upload Final Draft
              </Button>
            </Upload>
          </div>
          {renderDocList(finalDocs)}
        </div>
      </div>
    </div>
  );
};

export default PhaseDDraftUploads;
